/**
 * Tezam taxonomy on companies.category / event_exhibitors.category:
 * - Preferred delimiter: `|` (names often contain commas)
 * - Section (раздел):  "1. Кабели", "4. Другое"
 * - Category (категория): "1.1 Винодельческая продукция", "2.3. Гальванические покрытия"
 * - Plain text without numbers (demo / manual)
 */

const PREFIX_RE = /^\d+(?:\.\d+)*\.?\s*/u;
/** Leaf category: 1.1 Name or 1.1. Name or 1.01 Name (label may contain commas) */
const LEAF_RE = /^(\d+)\.(\d+)\.?\s+(.+)$/u;
/** Section: 1. Name — but not 1.1 Name */
const SECTION_RE = /^(\d+)\.\s+(.+)$/u;

export function stripCategoryPrefix(part: string): string {
  return part.replace(PREFIX_RE, "").trim();
}

/**
 * Split stored category blob into raw parts.
 * New sync uses `|`. Legacy comma lists only split before a new taxonomy code.
 */
export function splitCategoryRawParts(raw: string): string[] {
  const s = raw.trim();
  if (!s) return [];

  if (s.includes("|")) {
    return s
      .split("|")
      .map((p) => p.trim().replace(/\s+/g, " "))
      .filter(Boolean);
  }

  if (s.includes(";")) {
    return s
      .split(";")
      .map((p) => p.trim().replace(/\s+/g, " "))
      .filter(Boolean);
  }

  // Legacy: "1.2 Foo, bar, 1.3 Baz" — split only before next N. / N.M. token
  if (/\d+\./.test(s)) {
    return s
      .split(/,\s*(?=\d+(?:\.\d+)*\.?\s)/u)
      .map((p) => p.trim().replace(/\s+/g, " "))
      .filter(Boolean);
  }

  return s
    .split(/[,;]/)
    .map((p) => p.trim().replace(/\s+/g, " "))
    .filter(Boolean);
}

export type TaxonomyKind = "section" | "category" | "plain";

export type TaxonomyPart = {
  kind: TaxonomyKind;
  /** Section id: "1" | "_other" */
  sectionKey: string;
  /** Present for kind=section */
  sectionTitle?: string;
  /** Taxonomy code when known: "2.5" */
  code?: string;
  /** Stable key for category drill-down (section + label; codes merged) */
  categoryKey: string;
  /** Display label without taxonomy numbers */
  categoryLabel: string;
  raw: string;
};

function labelKey(label: string): string {
  return label.toLocaleLowerCase("ru-RU");
}

export function parseTaxonomyPart(raw: string): TaxonomyPart | null {
  const t = raw.trim().replace(/\s+/g, " ");
  if (!t) return null;

  const leaf = t.match(LEAF_RE);
  if (leaf) {
    const sectionKey = leaf[1]!;
    const code = `${leaf[1]}.${leaf[2]}`;
    const categoryLabel = leaf[3]!.trim();
    if (!categoryLabel) return null;
    return {
      kind: "category",
      sectionKey,
      code,
      categoryKey: `${sectionKey}::${labelKey(categoryLabel)}`,
      categoryLabel,
      raw: t,
    };
  }

  // Section only if single number before dot (not N.M)
  if (/^\d+\.\d+/.test(t)) {
    // Unusual: "1.1Something" without space — fall through to strip
  } else {
    const sec = t.match(SECTION_RE);
    if (sec) {
      const sectionKey = sec[1]!;
      const sectionTitle = sec[2]!.trim();
      if (!sectionTitle) return null;
      return {
        kind: "section",
        sectionKey,
        sectionTitle,
        categoryKey: `section:${sectionKey}`,
        categoryLabel: sectionTitle,
        raw: t,
      };
    }
  }

  const categoryLabel = stripCategoryPrefix(t) || t;
  return {
    kind: "plain",
    sectionKey: "_other",
    categoryKey: `plain:${labelKey(categoryLabel)}`,
    categoryLabel,
    raw: t,
  };
}

export function parseCompanyTaxonomy(raw: string | null | undefined): TaxonomyPart[] {
  if (!raw?.trim()) return [];
  const out: TaxonomyPart[] = [];
  const seen = new Set<string>();
  for (const chunk of splitCategoryRawParts(raw)) {
    const part = parseTaxonomyPart(chunk);
    if (!part) continue;
    const dedupe =
      part.kind === "section"
        ? `s:${part.sectionKey}`
        : `${part.categoryKey}|${part.code ?? ""}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    out.push(part);
  }
  return out;
}

/** Labels only (backward compatible). */
export function parseCompanyCategories(raw: string | null | undefined): string[] {
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const p of parseCompanyTaxonomy(raw)) {
    if (p.kind === "section") continue;
    const k = labelKey(p.categoryLabel);
    if (seen.has(k)) continue;
    seen.add(k);
    labels.push(p.categoryLabel);
  }
  return labels;
}

/** Display string without numeric prefixes. */
export function formatCategoriesDisplay(raw: string | null | undefined): string {
  return parseCompanyCategories(raw).join(", ");
}

export function companyMatchesCategory(
  rawCategory: string | null | undefined,
  selected: string,
): boolean {
  const needle = selected.trim().toLocaleLowerCase("ru-RU");
  if (!needle) return true;
  return parseCompanyTaxonomy(rawCategory).some(
    (p) =>
      p.categoryLabel.toLocaleLowerCase("ru-RU") === needle ||
      p.categoryKey === selected,
  );
}

export function companyInSection(
  rawCategory: string | null | undefined,
  sectionKey: string,
): boolean {
  return parseCompanyTaxonomy(rawCategory).some((p) => p.sectionKey === sectionKey);
}

export function companyMatchesTaxonomyCategory(
  rawCategory: string | null | undefined,
  categoryKey: string,
): boolean {
  return parseCompanyTaxonomy(rawCategory).some((p) => p.categoryKey === categoryKey);
}

export type TaxonomySectionRow = {
  key: string;
  title: string;
  categoryCount: number;
  companyCount: number;
};

export type TaxonomyCategoryRow = {
  key: string;
  label: string;
  sectionKey: string;
  /** Taxonomy codes merged into this row (e.g. 2.5 + 2.6 both "Другое") */
  codes: string[];
  companyCount: number;
};

/**
 * Build sections → categories graph from exhibitors at the current event.
 * Same display label inside a section is merged (Tezam often reuses "Другое" under different codes).
 * Section titles prefer explicit "N. Title" tags (incl. parent from sync); else "Раздел N".
 */
export function buildCompanyTaxonomy(companies: Array<{ category?: string | null }>): {
  sections: TaxonomySectionRow[];
  categoriesBySection: Map<string, TaxonomyCategoryRow[]>;
} {
  const sectionTitles = new Map<string, string>();
  const categoryMap = new Map<
    string,
    { label: string; sectionKey: string; codes: Set<string>; companies: Set<number> }
  >();
  const sectionCompanies = new Map<string, Set<number>>();

  companies.forEach((c, idx) => {
    const parts = parseCompanyTaxonomy(c.category);
    for (const p of parts) {
      if (p.kind === "section" && p.sectionTitle) {
        if (!sectionTitles.has(p.sectionKey)) {
          sectionTitles.set(p.sectionKey, p.sectionTitle);
        }
      }

      if (p.kind === "section") {
        let sc = sectionCompanies.get(p.sectionKey);
        if (!sc) {
          sc = new Set();
          sectionCompanies.set(p.sectionKey, sc);
        }
        sc.add(idx);
        continue;
      }

      let sc = sectionCompanies.get(p.sectionKey);
      if (!sc) {
        sc = new Set();
        sectionCompanies.set(p.sectionKey, sc);
      }
      sc.add(idx);

      let cat = categoryMap.get(p.categoryKey);
      if (!cat) {
        cat = {
          label: p.categoryLabel,
          sectionKey: p.sectionKey,
          codes: new Set(),
          companies: new Set(),
        };
        categoryMap.set(p.categoryKey, cat);
      }
      if (p.code) cat.codes.add(p.code);
      cat.companies.add(idx);
    }
  });

  const categoriesBySection = new Map<string, TaxonomyCategoryRow[]>();
  for (const [key, cat] of categoryMap) {
    const row: TaxonomyCategoryRow = {
      key,
      label: cat.label,
      sectionKey: cat.sectionKey,
      codes: Array.from(cat.codes).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
      companyCount: cat.companies.size,
    };
    const list = categoriesBySection.get(cat.sectionKey) ?? [];
    list.push(row);
    categoriesBySection.set(cat.sectionKey, list);
  }
  for (const [, list] of categoriesBySection) {
    list.sort((a, b) => a.label.localeCompare(b.label, "ru"));
  }

  const sectionKeys = new Set<string>([
    ...sectionCompanies.keys(),
    ...categoriesBySection.keys(),
  ]);

  const sections: TaxonomySectionRow[] = Array.from(sectionKeys).map((key) => {
    const cats = categoriesBySection.get(key) ?? [];
    const title =
      sectionTitles.get(key) ??
      (key === "_other" ? "Другое" : `Раздел ${key}`);
    return {
      key,
      title,
      categoryCount: cats.length,
      companyCount: sectionCompanies.get(key)?.size ?? 0,
    };
  });

  sections.sort((a, b) => {
    if (a.key === "_other") return 1;
    if (b.key === "_other") return -1;
    const an = Number(a.key);
    const bn = Number(b.key);
    if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return an - bn;
    return a.title.localeCompare(b.title, "ru");
  });

  return { sections, categoriesBySection };
}
